import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import IDVC from '@idscan/idvc2';
import { get, isEmpty, isNil } from 'lodash';

import { useConfig } from 'config/ConfigContext';

import './styles.scss';

const STEP_TIMEOUT_MS = 30000; // 30 seconds per step (fallback)

const IDScan = (props) => {
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const { disableManualUpload, idScanLicenseKey, idScanTimeout } = useConfig();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showFallbackNotice, setShowFallbackNotice] = useState(false);
  const [uploadFallbackVisible, setUploadFallbackVisible] = useState(false);
  const idvcRef = useRef(null);
  const stepTimerRef = useRef(null);
  const fallbackAppliedRef = useRef(false);
  const onSelfieStepRef = useRef(false);

  const getStepTimeoutMs = () => (idScanTimeout || STEP_TIMEOUT_MS);

  const clearStepTimer = () => {
    if (stepTimerRef.current) {
      clearTimeout(stepTimerRef.current);
      stepTimerRef.current = null;
    }
  };

  const enableFallback = () => {
    if (disableManualUpload) return;
    if (fallbackAppliedRef.current) return;
    fallbackAppliedRef.current = true;
    clearStepTimer();
    setShowFallbackNotice(true);
    setUploadFallbackVisible(true);
  };

  const startStepTimer = () => {
    clearStepTimer();
    if (disableManualUpload || onSelfieStepRef.current) return;
    stepTimerRef.current = setTimeout(() => {
      enableFallback();
    }, getStepTimeoutMs());
  };

  useEffect(() => {
    if (isNil(checkinData) || isEmpty(checkinData) || hasLoaded) {
      return;
    }

    let idvcInstance;

    const initIDVC = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (err) {
        console.error('Camera permission denied:', err);
        return;
      }

      setHasLoaded(true);

      const { handleSubmit, onReset, onRetakeHook } = props;
      const allowManualUpload = !disableManualUpload;
      const captureMode = { uploader: allowManualUpload, video: true };

      idvcRef.current = idvcInstance = new IDVC({
        networkUrl: '/networks',
        chunkPublicPath: '/networks',
        el: 'videoCapturingEl',
        licenseKey: idScanLicenseKey,
        resizeUploadedImage: 1500,
        fixFrontOrientAfterUpload: true,
        autoContinue: true,
        isShowDocumentTypeSelect: false,
        realFaceMode: 'all',
        useCDN: true,
        autoStart: true,
        documentTypes: [{
          type: 'DL',
          steps: [
            { type: 'front', name: 'Front', mode: captureMode },
            { type: 'pdf', name: 'Back', mode: captureMode },
            { type: 'face', name: 'Selfie', mode: { uploader: false, video: true } },
          ],
        }],
        onRetakeHook,
        onMounted() {
          startStepTimer();
        },
        onChange(data) {
          clearStepTimer();

          const completedType = data && data.step && data.step.type;
          if (completedType === 'pdf' || completedType === 'face') {
            onSelfieStepRef.current = true;
            setShowFallbackNotice(false);
          }

          if (!fallbackAppliedRef.current) {
            startStepTimer();
          }
        },
        submit(data) {
          clearStepTimer();

          const frontStep = data.steps.find((item) => item.type === 'front');
          const pdfStep = data.steps.find((item) => item.type === 'pdf');
          const faceStep = data.steps.find((item) => item.type === 'face');

          const { 2: frontImage } = frontStep.img.split(/:image\/(jpeg|png|webp);base64,/);
          const { 2: backImage } = pdfStep.img.split(/:image\/(jpeg|png|webp);base64,/);
          const { 2: faceImage } = faceStep.img.split(/:image\/(jpeg|png|webp);base64,/);

          const captureMethod = String(+(frontStep.source === 'video'))
              + String(+(pdfStep.source === 'video'))
              + String(+(faceStep.source === 'video'));

          handleSubmit({
            isDisabledVerifyButton: false,
            IDScanData: {
              frontImageBase64: frontImage,
              backOrSecondImageBase64: backImage,
              faceImageBase64: faceImage,
              documentType: data.documentType === 1 ? 'ID' : data.documentType,
              trackString: pdfStep.trackString,
              captureMethod,
              verifyFace: true,
              ssn: null,
              overriddenSettings: null,
              userAgent: window.navigator.userAgent,
              frontEndMetadata: null,
            },
          });
        },
        onReset,
        onCameraError(data) {
          console.log('cameraError', data);
        },
        onCaptureTimeout(data) {
          console.log('captureTimeout', data);
        }
      });
    };

    initIDVC();
    startStepTimer();

    return () => {
      if (idvcInstance && idvcInstance.destroy) {
        idvcInstance.destroy();
      }
      clearStepTimer();
    };
  }, [checkinData]);

  useEffect(() => {
    if (props.enableUploadFallback) {
      enableFallback();
    }
  }, [props.enableUploadFallback]);

  return (
    <>
      {showFallbackNotice && (
        <div className="idscan-fallback-notice">
          <p>Having trouble? You can now upload photos of your ID manually.</p>
        </div>
      )}
      <div
        id="videoCapturingEl"
        className={`${props.className} ${uploadFallbackVisible ? 'idscan-upload-fallback-visible' : 'idscan-upload-fallback-hidden'}`}
      />
    </>
  );
};

IDScan.propTypes = {
  className: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onRetakeHook: PropTypes.func,
  enableUploadFallback: PropTypes.bool,
};

IDScan.defaultProps = {
  className: '',
  onRetakeHook: () => {},
  enableUploadFallback: false,
};

export default IDScan;
